// ─────────────────────────────────────────────────────────────────────────────
// MetricsApp — the SurvAIHealth Metrics & Reporting application.
//
// Rendered as its own app space (the shared top switcher in App.js toggles
// between the Compliance Suite and this). It owns:
//   • a secondary horizontal nav: Reports | Grievance Log
//   • a facility scope selector ("All Facilities" rollup by default)
//   • Reports  → recursive report tree (left) + the selected report (right)
//   • Grievance Log → list / detail / create workflow
//
// All grievance data flows through getGrievances() (the stable feed seam),
// merged with locally-created grievances (localStorage, demo persistence).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { metricsNav, findFirstLeaf, flattenLeaves } from "./metricsNav";
import MetricsTreeNav from "./MetricsTreeNav";
import { getGrievances } from "./grievanceAdapter";
import { pccEnabled } from "./data/pccAdapter";
import { PILOT_FACILITIES, DEMO_METRICS_FACILITY, DEMO_METRICS_CCN, isDemoMetricsFacility } from "./grievanceModel";
import { REPORT_COMPONENTS, canViewMetrics } from "./reportRegistry";
import { REPORT_DEFS } from "./reportDefs";
import ReportEngine from "./ReportEngine";
import { loadCreatedGrievances, addCreatedGrievance } from "./grievanceStore";
import { ReportExportContext } from "./reports/_shared";
import { clearDatasetCache } from "./data/datasetCache";
import GrievanceLogManager from "./GrievanceLogManager";
import { facilityTimeZone } from "./reports/_shared";

// Same localStorage key shape the Compliance Suite's scanner views use, so the
// two halves of the product agree on what "sample mode" means. NOT imported
// from App.js: App renders this component, so importing back would be circular.
const PREVIEW_KEY = "survai_preview_mode_metrics";
function readSampleMode() {
  try { return localStorage.getItem(PREVIEW_KEY) === "on"; } catch { return false; }
}
function writeSampleMode(on) {
  try { localStorage.setItem(PREVIEW_KEY, on ? "on" : "off"); } catch { /* private mode */ }
}

// Live local time at the selected facility, derived from its state → time zone.
// Renders nothing when the facility's zone can't be determined (no state on file).
function FacilityClock({ facility }) {
  const tz = facilityTimeZone(facility);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!tz) return undefined;
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [tz]);
  if (!tz) return null;
  let label;
  try {
    label = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz, timeZoneName: "short" });
  } catch {
    return null;
  }
  return (
    <span
      title={`Facility local time — ${tz}${facility && facility.state ? ` (from ${facility.state})` : ""}`}
      style={{ fontSize: 12, color: "var(--ed-brand)", background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)", borderRadius: 8, padding: "6px 10px", fontWeight: 700, whiteSpace: "nowrap" }}
    >
      🕒 {label}
    </span>
  );
}

function useMobile(breakpoint = 640) {
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < breakpoint : false));
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
}

export default function MetricsApp({ session, facilities: scopedComplianceFacilities = null, canSeeAll = true, organizations = [] }) {
  const isMobile = useMobile();
  const firstLeaf = useMemo(() => findFirstLeaf(metricsNav), []);
  const allLeaves = useMemo(() => flattenLeaves(metricsNav), []);

  const [section, setSection] = useState("reports"); // reports | grievance-log
  const [selectedLeaf, setSelectedLeaf] = useState(firstLeaf);
  const [selectedOrgId, setSelectedOrgId] = useState("__all__"); // real org scope
  const [facilityCcn, setFacilityCcn] = useState("ALL");
  const [search, setSearch] = useState("");

  // Grievance data flows through the standard (non-PCC) seam — getGrievances +
  // locally-created entries, keyed by facility CCN — so it stays "mock".
  const orgSource = "mock";
  // Reports pull LIVE PointClickCare (+CMS) when the integration is enabled;
  // otherwise they fall back to the demo/sample datasets. Without this the whole
  // tab would silently serve sample data even on the live org. When live, a
  // facility with no PCC records shows the clear empty state rather than mock rows.
  // EXCEPTION: the St. Jude demo facility always uses mock, so there's a fully-
  // populated sample facility to explore even on a PCC-enabled org.
  // SAMPLE DATA ON ANY FACILITY, NOT JUST THE DEMO ONE.
  //
  // Sample data used to be reachable only by SELECTING the St. Jude demo
  // facility — which means you cannot show what a report looks like while the
  // facility you are actually discussing is on screen. That is the case it is
  // wanted in: a live facility whose reports are thin because the feed is a
  // training sandbox, where the question is "what does this look like with real
  // data in it?"
  //
  // Same switch as the Compliance Suite's scanner views, same localStorage key
  // shape, so the whole product moves together.
  const [sampleMode, setSampleMode] = useState(readSampleMode);
  const toggleSample = () => {
    const next = !sampleMode;
    writeSampleMode(next);
    setSampleMode(next);
    clearDatasetCache();          // sample and live rows must never mix
    setRefreshKey((k) => k + 1);
  };
  // ANY sample facility, not just St. Jude. This was an equality check against
  // one CCN, so a sample facility added later would have been asked for live
  // PointClickCare data it has no counterpart for and rendered empty.
  const reportsSource = (sampleMode || isDemoMetricsFacility(facilityCcn))
    ? "mock"
    : (pccEnabled() ? "pcc" : "mock");

  const [feedRecords, setFeedRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [created, setCreated] = useState(() => loadCreatedGrievances());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getGrievances({ facilityCcn })
      .then((rows) => { if (!cancelled) { setFeedRecords(rows); setLoading(false); } })
      .catch(() => { if (!cancelled) { setFeedRecords([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [facilityCcn, refreshKey]);

  // Every facility this user can access (same list as the Compliance Suite,
  // already scoped by the host App). Falls back to the demo pilots only when no
  // scoped list is passed; never leaks the pilots to a scoped (non-canSeeAll) user.
  const accessibleFacilities = useMemo(() => {
    let base;
    if (scopedComplianceFacilities) {
      if (scopedComplianceFacilities.length) base = scopedComplianceFacilities;
      else if (!canSeeAll) return [];
      else base = PILOT_FACILITIES;
    } else {
      base = PILOT_FACILITIES;
    }
    // Admin/VP always get the St. Jude demo facility at the top — a fully-
    // populated sample facility to explore — without leaking it to scoped staff.
    if (canSeeAll && DEMO_METRICS_FACILITY) {
      return [DEMO_METRICS_FACILITY, ...base.filter((f) => String(f.ccn) !== DEMO_METRICS_CCN)];
    }
    return base;
  }, [scopedComplianceFacilities, canSeeAll]);

  // Group accessible facilities by organization, mirroring the Compliance Suite's
  // org → facility split. Org display names come from the `organizations` prop;
  // facilities without an org id are grouped under a single "Facilities" bucket.
  const orgNameById = useMemo(
    () => new Map((organizations || []).map((o) => [o.id, o.name])),
    [organizations]
  );
  // Sample facilities carry an organization_id, so they form their own group —
  // selecting "St Margaret's Health" narrows the facility picker to its two
  // buildings, and every report filters by whichever is chosen.
  const orgGroups = useMemo(() => {
    const groups = new Map();
    for (const f of accessibleFacilities) {
      const oid = f.organization_id || "__noorg__";
      if (!groups.has(oid)) {
        groups.set(oid, {
          id: oid,
          name: oid === "__noorg__"
            ? "Facilities"
            : (orgNameById.get(oid) || String(oid).replace(/^org-/, "").replace(/-\d+$/, "").toUpperCase()),
          facilities: [],
        });
      }
      groups.get(oid).facilities.push(f);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [accessibleFacilities, orgNameById]);

  // Facilities visible in the picker = the selected org's facilities (or all of
  // them when "All Organizations" is selected).
  const facilities = useMemo(() => {
    if (selectedOrgId === "__all__") return accessibleFacilities;
    const g = orgGroups.find((o) => o.id === selectedOrgId);
    return g ? g.facilities : accessibleFacilities;
  }, [selectedOrgId, orgGroups, accessibleFacilities]);

  // If the user can only see one organization, lock the org selector to it.
  useEffect(() => {
    if (orgGroups.length === 1 && selectedOrgId !== orgGroups[0].id) {
      setSelectedOrgId(orgGroups[0].id);
    }
  }, [orgGroups, selectedOrgId]);

  // Auto-select the facility when the current scope has exactly one; otherwise,
  // if the previously selected facility fell out of scope (org changed), reset
  // to the "All Facilities" rollup.
  useEffect(() => {
    if (facilities.length === 1) {
      const only = String(facilities[0].ccn);
      if (facilityCcn !== only) setFacilityCcn(only);
    } else if (facilityCcn !== "ALL" && !facilities.some((f) => String(f.ccn) === String(facilityCcn))) {
      setFacilityCcn("ALL");
    }
  }, [facilities, facilityCcn]);

  // Access rule: data is always restricted to the facilities currently in scope.
  // Under the "All Facilities" rollup this is every facility the user can see
  // (and, when an org is selected, just that org's facilities).
  const scopeCcns = useMemo(
    () => new Set(facilities.map((f) => String(f.ccn))),
    [facilities]
  );

  // Merge feed + locally-created, created first, de-duped by id, scoped to access.
  const records = useMemo(() => {
    const inScope = (r) => facilityCcn !== "ALL"
      ? String(r.facilityCcn) === String(facilityCcn)
      : scopeCcns.has(String(r.facilityCcn));
    const scopedCreated = created.filter(inScope);
    const seen = new Set(scopedCreated.map((r) => r.id));
    return [...scopedCreated, ...feedRecords.filter((r) => !seen.has(r.id) && inScope(r))];
  }, [created, feedRecords, facilityCcn, scopeCcns]);

  const handleCreate = useCallback((record) => {
    setCreated(addCreatedGrievance(record));
  }, []);

  if (!canViewMetrics(session)) {
    return (
      <div style={{ maxWidth: 560, margin: "60px auto", textAlign: "center", color: "var(--ed-muted)" }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ed-ink)" }}>Metrics & Reporting is restricted</div>
        <div style={{ fontSize: 14, marginTop: 6 }}>Please sign in to view Metrics & Reporting.</div>
      </div>
    );
  }

  const key = selectedLeaf?.reportKey;
  const GrievanceReport = key && REPORT_COMPONENTS[key] ? REPORT_COMPONENTS[key] : null;
  const engineDef = key && !GrievanceReport ? REPORT_DEFS[key] : null;
  const facilityName = facilityCcn === "ALL"
    ? (selectedOrgId === "__all__" ? "All Facilities" : (orgGroups.find((o) => o.id === selectedOrgId)?.name || "All Facilities"))
    : (facilities.find((f) => String(f.ccn) === String(facilityCcn))?.name || facilityCcn);
  const filedBy = session?.name || "";

  const filteredLeaves = search.trim()
    ? allLeaves.filter((l) => l.label.toLowerCase().includes(search.trim().toLowerCase()))
    : null;

  const SECTIONS = [
    { id: "reports", label: "Reports", icon: "📈" },
    { id: "grievance-log", label: "Grievance Log", icon: "🗂" },
  ];

  // Supplied once here so no report has to carry it: the scope a report was run
  // over, and whether it is live or sample. Only the export reads it, and a
  // printed report that cannot say which facility and which data it came from
  // is worse than no printout.
  const exportCtx = { facilityName, generatedBy: filedBy, source: reportsSource };

  return (
    <ReportExportContext.Provider value={exportCtx}>
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: "#f0f2f5" }}>
      {/* App brand + secondary tabs */}
      <div style={{ background: "var(--ed-canvas)", borderBottom: "1px solid var(--ed-line)", padding: isMobile ? "10px 14px" : "12px 22px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 0, lineHeight: 1 }}>
            <span style={{ color: "var(--ed-ink)", fontWeight: 800, fontSize: 16 }}>SurvAI</span>
            <span style={{ color: "var(--ed-brand-lift)", fontWeight: 800, fontSize: 16 }}>Health</span>
          </div>
          <div style={{ color: "var(--ed-brand-lift)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".09em", marginTop: 2 }}>Metrics &amp; Reporting</div>
        </div>
        <div style={{ display: "flex", gap: 4, background: "var(--ed-line-2)", borderRadius: 9, padding: 4 }}>
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setSection(s.id)}
              style={{ border: "none", cursor: "pointer", fontFamily: "inherit", borderRadius: 6, padding: "7px 14px", fontSize: 13, fontWeight: 700,
                background: section === s.id ? "white" : "transparent",
                color: section === s.id ? "var(--ed-brand)" : "var(--ed-muted)",
                boxShadow: section === s.id ? "0 1px 3px rgba(0,0,0,.12)" : "none" }}>
              <span style={{ marginRight: 6 }}>{s.icon}</span>{s.label}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Organization split — same access model as the Compliance Suite.
              Multiple orgs → a selector; a single org → a static badge. */}
          {orgGroups.length > 1 ? (
            <select value={selectedOrgId} onChange={(e) => { setSelectedOrgId(e.target.value); setFacilityCcn("ALL"); }}
              title="Organization"
              style={{ border: "1px solid var(--ed-line-hover)", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "var(--ed-ink-2)", background: "var(--ed-canvas)", fontWeight: 600 }}>
              <option value="__all__">All Organizations</option>
              {orgGroups.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          ) : orgGroups.length === 1 ? (
            <div style={{ padding: "7px 12px", background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "var(--ed-brand)" }}>
              {orgGroups[0].name}
            </div>
          ) : null}
          <select value={facilityCcn} onChange={(e) => setFacilityCcn(e.target.value)}
            title="Facility"
            style={{ border: "1px solid var(--ed-line-hover)", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "var(--ed-ink-2)", background: "var(--ed-canvas)", fontWeight: 600 }}>
            <option value="ALL">All Facilities (rollup)</option>
            {facilities.map((f) => <option key={f.ccn} value={f.ccn}>{f.name} · {f.ccn}</option>)}
          </select>
          <div style={{ display: "inline-flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--ed-line-hover)" }}>
            {[{ on: false, label: "Live" }, { on: true, label: "Sample" }].map((opt) => (
              <button key={String(opt.on)} type="button"
                onClick={() => { if (sampleMode !== opt.on) toggleSample(); }}
                aria-pressed={sampleMode === opt.on}
                title={opt.on
                  ? "Show a fully-populated sample facility, whichever facility is selected"
                  : "Show this facility's own data"}
                style={{
                  border: "none", cursor: sampleMode === opt.on ? "default" : "pointer",
                  padding: "8px 13px", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                  background: sampleMode === opt.on ? (opt.on ? "#1d4ed8" : "var(--ed-brand)") : "var(--ed-canvas)",
                  color: sampleMode === opt.on ? "var(--ed-canvas)" : "var(--ed-ink-3)",
                }}>
                {opt.label}
              </button>
            ))}
          </div>
          <button onClick={() => { clearDatasetCache(); setRefreshKey((k) => k + 1); }} className="btn-secondary"
            title="Re-pull from PointClickCare. Reports are held for this session so moving between them costs nothing; this is what spends quota."
            style={{ fontSize: 12, padding: "8px 12px" }}>↻ Refresh</button>
          {facilityCcn !== "ALL" && <FacilityClock facility={facilities.find((f) => String(f.ccn) === String(facilityCcn))} />}
          <span style={{ fontSize: 12, color: "var(--ed-muted-2)" }}>{loading ? "Loading…" : `${records.length} record${records.length === 1 ? "" : "s"}`}</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: isMobile ? 14 : 22 }}>
        {section === "grievance-log" ? (
          <GrievanceLogManager records={records} facilityCcn={facilityCcn} facilityName={facilityName} facilities={facilities} source={orgSource} filedBy={filedBy} onCreate={handleCreate} />
        ) : (
          <div style={{ maxWidth: 1320, margin: "0 auto" }}>
            {/* Mobile report picker */}
            {isMobile && (
              <div className="card" style={{ padding: 12, marginBottom: 14 }}>
                <select value={selectedLeaf?.id || ""} onChange={(e) => { const leaf = allLeaves.find((l) => l.id === e.target.value); if (leaf) setSelectedLeaf(leaf); }}
                  style={{ width: "100%", border: "1px solid var(--ed-line-hover)", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "var(--ed-ink-2)", background: "var(--ed-canvas)" }}>
                  {allLeaves.map((l) => <option key={l.id} value={l.id}>{l.trail.length ? `${l.trail.join(" › ")} › ` : ""}{l.label}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: isMobile ? undefined : "248px 1fr", gap: 18, alignItems: "start" }}>
              {!isMobile && (
                <div className="card" style={{ padding: 10, position: "sticky", top: 12 }}>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reports…"
                    style={{ width: "100%", border: "1px solid var(--ed-line)", borderRadius: 7, padding: "8px 10px", fontSize: 13, color: "var(--ed-ink-2)", background: "#fafbfc", outline: "none", marginBottom: 8 }} />
                  {filteredLeaves ? (
                    filteredLeaves.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: "var(--ed-muted-2)", padding: "8px 10px" }}>No reports match “{search.trim()}”.</div>
                    ) : (
                      filteredLeaves.map((l) => (
                        <div key={l.id} role="button" tabIndex={0}
                          onClick={() => setSelectedLeaf(l)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedLeaf(l); } }}
                          style={{ padding: "8px 10px", borderRadius: 7, cursor: "pointer", fontSize: 13, marginBottom: 1,
                            fontWeight: selectedLeaf?.id === l.id ? 700 : 500,
                            color: selectedLeaf?.id === l.id ? "var(--ed-brand)" : "var(--ed-ink-3)",
                            background: selectedLeaf?.id === l.id ? "var(--ed-brand-soft)" : "transparent" }}>
                          📄 {l.label}
                        </div>
                      ))
                    )
                  ) : (
                    <MetricsTreeNav nav={metricsNav} selectedLeafId={selectedLeaf?.id} onSelect={(leaf) => setSelectedLeaf(leaf)} />
                  )}
                </div>
              )}

              <div style={{ minWidth: 0 }}>
                {GrievanceReport ? (
                  loading ? (
                    <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ed-muted-2)" }}>Loading grievance data…</div>
                  ) : (
                    <GrievanceReport records={records} facilityCcn={facilityCcn} facilityName={facilityName} session={session} />
                  )
                ) : engineDef ? (
                  <ReportEngine def={engineDef} title={selectedLeaf?.label} facilityCcn={facilityCcn} facilityName={facilityName} source={reportsSource} refreshKey={refreshKey} session={session} />
                ) : (
                  <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ed-muted)" }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🚧</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ed-ink)" }}>Report not available</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>This report has not been wired up yet. Pick another from the menu.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </ReportExportContext.Provider>
  );
}
